/**
 * Phase 26 — under a Block action: which groups the block holds, by their
 * list of the day's tags (user, 12/10/2026: "the ones who has these tags can
 * go through even if its blocked hours, and vice versa"). Every list · only
 * lists tagged … · all lists except those tagged …; the tags are picked in
 * the shared checkbox dropdown (user: "it must be a checkbox dropdown"), the
 * list tag catalog in three bands — performance (Tag Rules › Lists), static,
 * system (New / Unused) — each row with its colour and its kind.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo } from 'react';
import { DROP_GEN_RULE_LIMITS, type DropGenRuleScope, type DropGenTagKind, type DropGenTagOption } from '@trafficpulse/shared';

import { MultiSearchableSelect, type SearchableSelectOption } from '@/components/ui';

import { TagDot, useRuleTagCatalog } from '../tags';

type Lists = 'all' | DropGenRuleScope['lists'];
const LISTS: ReadonlyArray<{ value: Lists; label: string }> = [
  { value: 'all', label: 'every list' },
  { value: 'only_tagged', label: 'only lists tagged…' },
  { value: 'except_tagged', label: 'all lists except those tagged…' },
];
const KIND_ORDER: ReadonlyArray<DropGenTagKind | undefined> = ['performance', 'static', 'system', undefined];
const MAX = DROP_GEN_RULE_LIMITS.scopeTags;

/** At most three names, then "or N more" — the card's meaning line stays one sentence. */
function tagWords(tags: string[]): string {
  const shown = tags.slice(0, 3).map((t) => `"${t}"`);
  const rest = tags.length - shown.length;
  return rest > 0 ? `${shown.join(', ')} or ${rest} more` : shown.join(' or ');
}

/** What the scope does, in one sentence — the card's meaning line (nothing while no tag is picked: the row says so). */
export function scopeMeaning(scope: DropGenRuleScope | undefined): string {
  if (!scope || scope.tags.length === 0) return '';
  const names = tagWords(scope.tags);
  return scope.lists === 'only_tagged'
    ? `Only the groups whose list of the day is tagged ${names} are held — the others send. A list whose tags cannot be read goes through.`
    : `The groups whose list of the day is tagged ${names} go through — the others are held. A list whose tags cannot be read goes through.`;
}

/** The catalog as dropdown rows: performance tags first, then static, then system; colour dot + kind on every row. */
export function scopeTagOptions(catalog: DropGenTagOption[] | undefined): SearchableSelectOption<string>[] {
  return KIND_ORDER.flatMap((kind) => (catalog ?? []).filter((t) => t.kind === kind)).map((t) => ({
    value: t.name,
    label: t.name,
    optionContent: (
      <span className="flex min-w-0 items-center gap-2">
        <TagDot color={t.color} className="h-2 w-2" />
        <span className="truncate">{t.name}</span>
        {t.kind ? <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">{t.kind}</span> : null}
      </span>
    ),
  }));
}

interface Props {
  scope: DropGenRuleScope | undefined;
  onChange: (scope: DropGenRuleScope | undefined) => void;
}

export const RuleScopeRow: FC<Props> = ({ scope, onChange }) => {
  const { data: catalog } = useRuleTagCatalog('lists');
  const options = useMemo(() => scopeTagOptions(catalog), [catalog]);
  const lists: Lists = scope?.lists ?? 'all';
  const setLists = (next: Lists) => onChange(next === 'all' ? undefined : { lists: next, tags: scope?.tags ?? [] });
  const setTags = (tags: string[]) => (scope ? onChange({ ...scope, tags: tags.slice(0, MAX) }) : undefined);
  const empty = catalog !== undefined && catalog.length === 0;
  return (
    <div className="flex flex-col gap-1.5" data-testid="rule-scope">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-muted-foreground">for</span>
        <select value={lists} onChange={(e) => setLists(e.target.value as Lists)} aria-label="Which lists the block holds" className="h-7 rounded-md border border-input bg-background px-2 text-xs">
          {LISTS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {scope ? (
          <MultiSearchableSelect
            options={options}
            value={scope.tags}
            onChange={setTags}
            placeholder="Pick the list tags"
            summaryNoun="tags"
            searchPlaceholder="Search list tags…"
            disabled={empty}
            className="h-7 w-auto min-w-[11rem] max-w-[18rem] rounded-md px-2 text-xs"
          />
        ) : null}
      </div>
      {scope && empty ? <span className="text-[11px] text-muted-foreground">No list tags yet — create them on Tag Rules › Lists or Static.</span> : null}
      {scope && scope.tags.length === 0 && !empty ? <span className="text-[11px] text-warning">Pick at least one tag — until then this rule does nothing.</span> : null}
      {scope && scope.tags.length >= MAX ? <span className="text-[11px] text-muted-foreground">A block tests at most {MAX} tags.</span> : null}
    </div>
  );
};
